import some from 'lodash-es/some';

import { get as getHomepageConfiguration } from '@rover/react-lib/src/apis/homepageConfiguration.api';
import {
  get as getServiceTypeDescriptionsApi,
  LocationParams,
} from '@rover/react-lib/src/apis/homepageServiceTypes.api';
import { get as getRebookingProfiles } from '@rover/react-lib/src/apis/rebooking.api';
import { selectors as locationInputSelectors } from '@rover/react-lib/src/components/formFields/LocationInput/LocationInput.duck';
import EXPERIMENTS from '@rover/react-lib/src/constants/experiments.constants';
import type {
  ActionCreator,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { effects as countryFooterLinksEffects } from '@rover/react-lib/src/redux/ducks/countryFooterLinks.duck';
import { effects as countryRegionLinksEffects } from '@rover/react-lib/src/redux/ducks/countryRegionLinks.duck';
import {
  effects as currentUserEffects,
  selectors as currentUserSelectors,
} from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import { selectors as experimentsSelectors } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { selectors as petsSelectors } from '@rover/react-lib/src/redux/ducks/pets.duck';
import {
  effects as serviceTypeEffect,
  selectors as serviceTypeSelectors,
} from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { HomepageRebookingProfile } from '@rover/rsdk/src/apiClient/latest';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import {
  captureException,
  captureMessage,
  withScope,
} from '@rover/rsdk/src/modules/ErrorReporting/sentry';
import { ServiceType } from '@rover/types';
import type {
  HomepageConfiguration,
  HomepageMetaTags,
} from '@rover/types/src/HomepageConfiguration';
import { Pet, PetType } from '@rover/types/src/Pet';
import { ServiceTypeSlugs } from '@rover/types/src/Service';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

import { effects as searchBoxEffects } from './components/SearchBox/SearchBox.duck';
import {
  DEFAULT_HEADLINE_TEXT,
  DEFAULT_SUBLINE_TEXT,
  LOCATION_INPUT_ID,
} from './HomePage.constants';
import ServiceTypeDescription from './ServiceTypeDescription';

export type State = {
  fetchingServiceTypeDescriptions: boolean;
  homepageConfiguration: HomepageConfiguration;
  locationName: string | null;
  petTypes: Record<Exclude<PetType, PetType.PUPPY>, boolean>;
  serviceTypeDescriptions: ServiceTypeDescription[];
  fetchingRebooking: boolean;
  rebookingProfiles: HomepageRebookingProfile[];
};

type Actions = {
  fetchServiceTypeDescriptionsPending: ActionCreator<void>;
  setHomepageConfiguration: ActionCreator<HomepageConfiguration>;
  setLocationName: ActionCreator<string | null>;
  setPetType: ActionCreator<[PetType, boolean]>;
  setServiceTypeDescriptions: ActionCreator<ServiceTypeDescription[]>;
  fetchRebookingProfilesSuccess: ActionCreator<HomepageRebookingProfile[]>;
  fetchRebookingProfilesError: ActionCreator<void>;
  fetchRebookingProfilesPending: ActionCreator<void>;
};

type Effects = {
  prefetchData: EffectCreator<void>;
  fetchHomepageConfiguration: EffectCreator<void>;
  fetchRebookingProfiles: EffectCreator<boolean>;
  fetchServiceTypeDescriptions: EffectCreator<LocationParams | null | undefined>;
  handleGeocodeFailure: EffectCreator<void>;
  handleLocationChange: EffectCreator<
    Omit<LocationParams, 'locationName'> & {
      value: string;
    }
  >;
  handlePetTypeChange: EffectCreator<[PetType, boolean]>;
};

type Selectors = {
  getHasActivePets: SelectorCreator<boolean>;
  getHeadlineText: SelectorCreator<string>;
  getHomepageConfiguration: SelectorCreator<HomepageConfiguration>;
  getLocationName: SelectorCreator<string | null>;
  getPetTypes: SelectorCreator<State['petTypes']>;
  getSelectedPetTypes: SelectorCreator<PetType[]>;
  getServiceTypeDescriptions: SelectorCreator<ServiceTypeDescription[]>;
  getSubheadlineText: SelectorCreator<string>;
  isFetchingServiceTypeDescriptions: SelectorCreator<boolean>;
  shouldDisableServiceTypeButtons: SelectorCreator<boolean>;
  getLocalizedHelpLink: SelectorCreator<string>;
  getShouldShowTrustpilotBadgeStatic: SelectorCreator<boolean>;
  getRebookingProfiles: SelectorCreator<HomepageRebookingProfile[]>;
  getPetTypeBrowsableServiceTypeOptions: SelectorCreator<ServiceTypeDescription[]>;
  getMetaTags: SelectorCreator<HomepageMetaTags>;
  getIsRolloutRoverProtectCh2Active: SelectorCreator<boolean>;
  getIsRoverProtectCh2SurveyDisabled: SelectorCreator<boolean>;
  getIsRolloutRoverProtectCh2Experiment: SelectorCreator<(arg0: string) => boolean>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'homePage',
  initialState: {
    fetchingRebooking: true,
    rebookingProfiles: [],
    locationName: null,
    serviceTypeDescriptions: [
      ServiceTypeSlugs.overnightBoarding,
      ServiceTypeSlugs.overnightTraveling,
      ServiceTypeSlugs.dropIn,
      ServiceTypeSlugs.doggyDayCare,
      ServiceTypeSlugs.dogWalking,
      ServiceTypeSlugs.dogTraining,
    ].map((slug) => new ServiceTypeDescription({ slug })),
    petTypes: { dog: true, cat: false },
    homepageConfiguration: {
      headlineText: DEFAULT_HEADLINE_TEXT(),
      subHeadlineText: DEFAULT_SUBLINE_TEXT(),
      locationName: null,
      hasActivePets: false,
      localizedHelpLink: '',
      title: '',
      metaDescription: '',
    },
    fetchingServiceTypeDescriptions: false,
  },
  actions: {
    setLocationName: (locationName) => (state) => ({ ...state, locationName }),
    fetchRebookingProfilesError: () => (state) => ({
      ...state,
      fetchingRebooking: false,
      rebookingProfiles: [],
    }),
    fetchRebookingProfilesPending: () => (state) => ({
      ...state,
      fetchingRebooking: true,
    }),
    fetchRebookingProfilesSuccess: (rebookingProfiles) => (state) => ({
      ...state,
      fetchingRebooking: false,
      rebookingProfiles,
    }),
    fetchServiceTypeDescriptionsPending: () => (state) => ({
      ...state,
      fetchingServiceTypeDescriptions: true,
    }),
    setServiceTypeDescriptions: (serviceTypeDescriptions) => (state) => ({
      ...state,
      serviceTypeDescriptions,
      fetchingServiceTypeDescriptions: false,
    }),
    setPetType:
      ([petType, value]) =>
      (state) => {
        try {
          fireDataLayerEvent({
            event: 'navigation-pet-selector',
            species: petType,
            toggleState: value ? 'on' : 'off',
          });
        } catch (e) {
          captureError(e as Error);
        }

        return {
          ...state,
          petTypes: { ...state.petTypes, [petType]: value },
        };
      },
    setHomepageConfiguration: (homepageConfiguration) => (state) => ({
      ...state,
      homepageConfiguration,
    }),
  },
  effects: {
    prefetchData:
      () =>
      async (
        dispatch,
        getState,
        actions,
        { fetchHomepageConfiguration, fetchServiceTypeDescriptions, fetchRebookingProfiles }
      ): Promise<void> => {
        await dispatch(currentUserEffects.fetchCurrentUser());
        const isAuthenticated = currentUserSelectors.isAuthenticated(getState());
        const promises = [
          dispatch(fetchHomepageConfiguration()),
          dispatch(fetchServiceTypeDescriptions()),
          dispatch(countryFooterLinksEffects.fetchCountryFooterLinks()),
          dispatch(countryRegionLinksEffects.fetchCountryRegionLinks()),
          dispatch(serviceTypeEffect.fetchServiceTypes()),
        ];
        if (isAuthenticated) {
          promises.push(dispatch(fetchRebookingProfiles()));
          promises.push(dispatch(searchBoxEffects.fetchPetsAndUpdateFilters()));
        }
        await Promise.all(promises);
      },
    fetchRebookingProfiles:
      () =>
      async (
        dispatch,
        getState,
        {
          fetchRebookingProfilesSuccess,
          fetchRebookingProfilesError,
          fetchRebookingProfilesPending,
        }
      ) => {
        dispatch(fetchRebookingProfilesPending());
        try {
          const rebookingProfiles = await getRebookingProfiles();
          dispatch(fetchRebookingProfilesSuccess(rebookingProfiles.data));
        } catch (e) {
          dispatch(fetchRebookingProfilesError());
        }
      },
    fetchHomepageConfiguration:
      () =>
      async (dispatch, getState, { setHomepageConfiguration, setLocationName }) => {
        try {
          const homepageConfiguration = await getHomepageConfiguration();
          if (homepageConfiguration) {
            dispatch(setHomepageConfiguration(homepageConfiguration));
            dispatch(setLocationName(homepageConfiguration.locationName));
          } else {
            captureMessage('Homepage configuration request was successful but response was null');
          }
        } catch (e) {
          // We have a safe default when this happens so we can just report it
          // rely on the default values
          captureException(e as Error);
        }
      },
    fetchServiceTypeDescriptions:
      (locationParams) =>
      async (
        dispatch,
        getState,
        { setServiceTypeDescriptions, fetchServiceTypeDescriptionsPending },
        effects,
        { getSelectedPetTypes }
      ) => {
        dispatch(fetchServiceTypeDescriptionsPending());
        const selectedPetTypes = getSelectedPetTypes(getState());
        try {
          const serviceTypeDescriptions = await getServiceTypeDescriptionsApi(
            selectedPetTypes,
            locationParams
          );
          const newServiceTypeDescriptions = serviceTypeDescriptions.map(({ slug, webAction }) => {
            let updatedWebAction = webAction;
            if (updatedWebAction?.includes('/search')) {
              const params = webAction.split('&');
              const petType = params
                .find((param) => param?.includes('pet_type'))
                ?.split('=')[1] as PetType;
              if (!updatedWebAction?.includes('dog_count')) {
                if (petType?.includes(PetType.DOG)) {
                  updatedWebAction = `${updatedWebAction}&dog_count=1`;
                }
              }
              if (!updatedWebAction?.includes('cat_count')) {
                if (petType?.includes(PetType.CAT)) {
                  updatedWebAction = `${updatedWebAction}&cat_count=1`;
                }
              }
            }
            return new ServiceTypeDescription({ slug, webAction: updatedWebAction });
          });
          dispatch(setServiceTypeDescriptions(newServiceTypeDescriptions));
        } catch (e) {
          withScope((scope) => {
            scope.setExtras({ selectedPetTypes, locationParams });
            captureException(e as Error);
          });
          // We revert the service types to the default
          // so that we don't have to surface a specific
          // error to the user on the home page and the UX
          // doesn't break (they can still click on whatever
          // thing they selected originally)
          dispatch(setServiceTypeDescriptions(options.initialState.serviceTypeDescriptions));
        }
      },

    handleLocationChange:
      ({ value: locationName, ...other }) =>
      (dispatch, getState, { setLocationName }, { fetchServiceTypeDescriptions }) => {
        dispatch(setLocationName(locationName));
        const locationParams = { ...other, locationName };
        dispatch(fetchServiceTypeDescriptions(locationParams));
        try {
          fireDataLayerEvent({
            event: 'navigation-select-location',
            centerLat: locationParams.lat,
            centerLong: locationParams.lng,
            postalCode: locationParams.postalCode,
            country: locationParams.countryCode,
            locationName: locationParams.locationName,
          });
        } catch (e) {
          captureError(e as Error);
        }
      },

    handlePetTypeChange:
      (nextPetType) =>
      async (dispatch, getState, { setPetType }, { fetchServiceTypeDescriptions }) => {
        await dispatch(setPetType(nextPetType));
        dispatch(fetchServiceTypeDescriptions());
      },

    handleGeocodeFailure:
      () =>
      (dispatch, getState, { setServiceTypeDescriptions, setLocationName }) => {
        dispatch(setServiceTypeDescriptions(options.initialState.serviceTypeDescriptions));
        dispatch(setLocationName(''));
      },
  },
  selectors: {
    getServiceTypeDescriptions: (getState, createSelector) =>
      createSelector([getState], (x) => x.serviceTypeDescriptions),
    getPetTypeBrowsableServiceTypeOptions: (getState, createSelector) =>
      createSelector(
        [getState, serviceTypeSelectors.getPetTypeBrowsableServiceTypes, petsSelectors.getPets],
        (x, getPetTypeBrowsableServices: (petTypes: PetType[]) => ServiceType[], pets: Pet[]) => {
          const petTypeBrowsableServices = getPetTypeBrowsableServices(pets.map((p) => p.petType));

          const serviceTypeDescriptors = x.serviceTypeDescriptions;

          const uniqueMapped = (
            services: ServiceType[]
          ): Array<ServiceTypeDescription | undefined> =>
            [...new Set(services)].map((s) =>
              serviceTypeDescriptors.find((st) => st.slug === s.slug)
            );
          return uniqueMapped(petTypeBrowsableServices).filter((sd) => sd);
          return [];
        }
      ),
    getRebookingProfiles: (getState, createSelector) =>
      createSelector([getState], (x) => x.rebookingProfiles),
    getLocationName: (getState, createSelector) =>
      createSelector([getState], (x) => x.locationName),

    getPetTypes: (getState, createSelector) => createSelector([getState], (x) => x.petTypes),
    getSelectedPetTypes: (getState, createSelector, { getPetTypes }) =>
      createSelector([getPetTypes], (petTypes) =>
        Object.entries(petTypes).reduce(
          (pts, [petType, selected]) => (selected ? [...pts, petType as unknown as PetType] : pts),
          [] as PetType[]
        )
      ),
    getHomepageConfiguration: (getState, createSelector) =>
      createSelector([getState], (x) => x.homepageConfiguration),
    getHeadlineText: (
      getState,
      createSelector,
      { getHomepageConfiguration: selectHomepageConfiguration }
    ) => createSelector([selectHomepageConfiguration], (x) => x.headlineText),
    getSubheadlineText: (
      getState,
      createSelector,
      { getHomepageConfiguration: selectHomepageConfiguration }
    ) => createSelector([selectHomepageConfiguration], (x) => x.subHeadlineText),
    getHasActivePets: (
      getState,
      createSelector,
      { getHomepageConfiguration: selectHomepageConfiguration }
    ) => createSelector([selectHomepageConfiguration], (x) => x.hasActivePets),

    isFetchingServiceTypeDescriptions: (getState, createSelector) =>
      createSelector([getState], (x) => x.fetchingServiceTypeDescriptions),
    getShouldShowTrustpilotBadgeStatic: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT_TRUSTPILOT_BADGE_STATIC_IMAGE)
      ),
    shouldDisableServiceTypeButtons: (
      getState,
      createSelector,
      { isFetchingServiceTypeDescriptions }
    ) =>
      createSelector(
        [
          isFetchingServiceTypeDescriptions,
          (state) =>
            // @ts-expect-error
            locationInputSelectors.getLocationGeocodeResultsAreLoading(state, LOCATION_INPUT_ID),
        ],
        (...args) => {
          return some(args);
        }
      ),
    getLocalizedHelpLink: (
      getState,
      createSelector,
      { getHomepageConfiguration: selectHomepageConfiguration }
    ) => createSelector([selectHomepageConfiguration], (x) => x.localizedHelpLink),
    getMetaTags: (getState, createSelector) =>
      createSelector([getState], (x) => ({
        title: x.homepageConfiguration.title,
        metaDescription: x.homepageConfiguration.metaDescription,
      })),
    getIsRolloutRoverProtectCh2Active: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT_ROVER_PROTECT_CH_2)
      ),
    getIsRoverProtectCh2SurveyDisabled: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.KILLSWITCH_ROVER_PROTECT_CH_2_SURVEY)
      ),
    getIsRolloutRoverProtectCh2Experiment: (getState, createSelector) =>
      createSelector(
        [experimentsSelectors.isCustomVariantGroup],
        (isCustomVariantGroup) => (variantName) =>
          isCustomVariantGroup(EXPERIMENTS.ROLLOUT_ROVER_PROTECT_CH_2_EXPERIMENT.value, variantName)
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options);
